import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/flights/fare-classes';

/**
 * Map API fare class to UI format
 * API: { id, fareClassCode, fareClassName, airlineId, cabinClassId, active, airlineName, iataCode, cabinClassName, cabinClassCode, ... }
 * UI:  { id, fareClassCode, fareClassName, airlineId, cabinClassId, airline, airlineCode, cabinClass, cabinClassCode, status }
 */
export const mapApiToUi = (api) => {
  if (!api) return null;
  return {
    id: api.id,
    fareClassCode: api.fareClassCode ?? '',
    fareClassName: api.fareClassName ?? '',
    airlineId: api.airlineId,
    cabinClassId: api.cabinClassId,
    airline: api.airlineName ?? '',
    airlineCode: api.iataCode ?? '',
    cabinClass: api.cabinClassName ?? '',
    cabinClassCode: api.cabinClassCode ?? '',
    flightScope: api.flightScope ?? '',
    fareType: api.fareType ?? '',
    checkinStatus: api.checkinStatus ?? '',
    checkinRequiredFields: api.checkinRequiredFields
      ? (typeof api.checkinRequiredFields === 'string' ? JSON.parse(api.checkinRequiredFields) : api.checkinRequiredFields)
      : [],
    checkinText: api.checkinText ?? '',
    checkinUrl: api.checkinUrl ?? '',
    // Stored as a JSON array, edited as a comma separated box. Kept as an array
    // here so the screen can count them; the form joins it for display.
    bookingClassCodes: (() => {
      const raw = api.bookingClassCodes;
      if (!raw) return [];
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })(),
    status: api.active ? 'active' : 'inactive',
  };
};

/**
 * Map UI fare class to API format for create/update
 */
export const mapUiToApi = (ui) => ({
  fareClassCode: String(ui.fareClassCode ?? '').toUpperCase(),
  fareClassName: String(ui.fareClassName ?? '').trim(),
  airlineId: parseInt(ui.airlineId, 10) || ui.airlineId,
  cabinClassId: parseInt(ui.cabinClassId, 10) || ui.cabinClassId,
  flightScope: ui.flightScope,
  fareType: ui.fareType,
  checkinStatus: ui.checkinStatus || null,
  checkinRequiredFields: ui.checkinStatus ? (ui.checkinRequiredFields || []) : [],
  checkinText: ui.checkinStatus ? String(ui.checkinText ?? '').trim() : '',
  checkinUrl: ui.checkinStatus ? String(ui.checkinUrl ?? '').trim() : '',
  // Sent as typed. The server splits, upper-cases and de-duplicates, so the
  // rule lives in one place rather than being half-enforced on each side.
  bookingClassCodes: ui.bookingClassCodes ?? '',
  active: ui.status === 'active' || ui.active === true,
});

/**
 * useGetFareClasses - fetch fare classes with params
 * @param {Object} params - { page, limit, search, airline, cabinClass, status }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetFareClasses = (params = {}) => {
  const {
    page = 1,
    limit = 100,
    search = '',
    airline = '',
    cabinClass = '',
    status = '',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFareClasses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit };
      if (search) queryParams.search = search;
      if (airline) queryParams.airline = airline; // airlineId for filter
      if (cabinClass) queryParams.cabinClass = cabinClass; // cabinClassId for filter
      if (status && status !== 'all') {
        queryParams.status = status === 'active' ? 'Active' : 'Inactive';
      }

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData((res.data || []).map(mapApiToUi));
        if (res.pagination) setPagination(res.pagination);
      } else {
        setError(res.message || 'Failed to load fare classes');
        setData([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load fare classes');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, airline, cabinClass, status]);

  useEffect(() => {
    fetchFareClasses();
  }, [fetchFareClasses]);

  return { data, pagination, loading, error, refetch: fetchFareClasses };
};

export const useCreateFareClass = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (payload) => {
    setLoading(true);
    try {
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.post(BASE, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to create fare class' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to create fare class' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useUpdateFareClass = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    try {
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.put(`${BASE}/${id}`, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to update fare class' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to update fare class' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useDeleteFareClass = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to delete fare class' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};
